import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  FileSpreadsheet,
  Layers3,
  ListPlus,
  Loader2,
  Plus,
  Search,
  TableProperties,
  Trash2,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { adminApi, type AdminContent, type HierarchyLevel } from '@/api/admin'
import {
  printBookApi,
  type PrintBookBulkCreateRequest,
  type PrintBookMaterialType,
  type PrintBookScopeLevel,
  type PrintBookSourceType,
} from '@/api/printBook'
import { ContentLevelBadge, ContentPickerModal } from '@/components/content/ContentPickerModal'
import { BookImageUploadField } from './BookImageUploadField'
import { BookBulkModulePicker } from './BookBulkModulePicker'
import { parsePrintBookBulkCsv } from './printBookBulkCsv'
import {
  buildPrintBookManualRows,
  collectDescendantModules,
  type PrintBookManualDraft,
} from './printBookBulkManual'

const CSV_EXAMPLE = `orderIndex,volumeLabel,title,moduleIds
1,1권,Core A 체크업북 1권,101|102|103
2,2권,Core A 체크업북 2권,104|105|106`

type InputMode = 'manual' | 'csv'

function createManualDraft(): PrintBookManualDraft {
  return {
    id: crypto.randomUUID(),
    volumeLabel: '',
    title: '',
    moduleIds: [],
  }
}

export function BookBulkCreatePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [bookLabel, setBookLabel] = useState('')
  const [materialType, setMaterialType] = useState<PrintBookMaterialType | ''>('CHECKUP_BOOK')
  const [scopeLevel, setScopeLevel] = useState<PrintBookScopeLevel | ''>('GLOBAL')
  const [scopeContent, setScopeContent] = useState<AdminContent | null>(null)
  const [isScopePickerOpen, setIsScopePickerOpen] = useState(false)
  const [sourceType, setSourceType] = useState<PrintBookSourceType>('GENERATED_PREVIEW')
  const [titlePattern, setTitlePattern] = useState('{bookLabel} {volumeLabel}')
  const [subtitle, setSubtitle] = useState('')
  const [description, setDescription] = useState('')
  const [learnerCoverImageUrl, setLearnerCoverImageUrl] = useState<string | null>(null)
  const [noticeMessage, setNoticeMessage] = useState('')
  const [samplePdfUrl, setSamplePdfUrl] = useState('')
  const [inputMode, setInputMode] = useState<InputMode>('manual')
  const [csvText, setCsvText] = useState(CSV_EXAMPLE)
  const [manualDrafts, setManualDrafts] = useState<PrintBookManualDraft[]>(() => [createManualDraft()])
  const [modulePickerDraftId, setModulePickerDraftId] = useState<string | null>(null)
  const [previewRequested, setPreviewRequested] = useState(false)

  const csvParsed = useMemo(
    () => parsePrintBookBulkCsv(csvText, { bookLabel, titlePattern }),
    [bookLabel, csvText, titlePattern],
  )
  const manualParsed = useMemo(
    () => buildPrintBookManualRows(manualDrafts, { bookLabel, titlePattern }),
    [bookLabel, manualDrafts, titlePattern],
  )
  const parsed = inputMode === 'csv' ? csvParsed : manualParsed
  const sortedRows = useMemo(
    () => [...parsed.rows].sort((left, right) => left.orderIndex - right.orderIndex),
    [parsed.rows],
  )

  const hasScopedContent = scopeLevel === 'CURRICULUM' || scopeLevel === 'COURSE'
  const { data: scopeHierarchy, isLoading: isHierarchyLoading, isError: isHierarchyError } = useQuery({
    queryKey: ['admin-content-hierarchy', scopeContent?.id],
    queryFn: () => adminApi.getContentHierarchy(scopeContent!.id, false),
    enabled: inputMode === 'manual' && hasScopedContent && scopeContent != null,
  })
  const moduleOptions = useMemo(
    () => scopeHierarchy ? collectDescendantModules(scopeHierarchy) : [],
    [scopeHierarchy],
  )
  const modulesById = useMemo(
    () => new Map(moduleOptions.map((module) => [module.id, module])),
    [moduleOptions],
  )
  const activeModuleDraft = manualDrafts.find((draft) => draft.id === modulePickerDraftId) ?? null

  const createMutation = useMutation({
    mutationFn: (request: PrintBookBulkCreateRequest) => printBookApi.bulkCreate(request),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['print-books'] })
      toast.success(`${sortedRows.length}권의 교재를 생성했습니다.`)
      navigate('/contents/books')
    },
    onError: () => toast.error('교재 일괄 생성에 실패했습니다. 입력 내용을 확인해 주세요.'),
  })

  const canSubmit = previewRequested
    && !parsed.hasErrors
    && sortedRows.length > 0
    && bookLabel.trim().length > 0
    && (!hasScopedContent || scopeContent != null)

  const clearSelectedModules = () => {
    setManualDrafts((current) => current.map((draft) => ({ ...draft, moduleIds: [] })))
  }

  const handleScopeLevelChange = (nextScopeLevel: PrintBookScopeLevel | '') => {
    setScopeLevel(nextScopeLevel)
    setScopeContent(null)
    clearSelectedModules()
  }

  const handleScopeContentSelect = (content: AdminContent) => {
    setScopeContent(content)
    setIsScopePickerOpen(false)
    clearSelectedModules()
  }

  const updateManualDraft = (draftId: string, patch: Partial<PrintBookManualDraft>) => {
    setManualDrafts((current) => current.map((draft) => (
      draft.id === draftId ? { ...draft, ...patch } : draft
    )))
  }

  const removeManualDraft = (draftId: string) => {
    setManualDrafts((current) => current.filter((draft) => draft.id !== draftId))
    if (modulePickerDraftId === draftId) setModulePickerDraftId(null)
  }

  const handleSubmit = () => {
    const request: PrintBookBulkCreateRequest = {
      titlePattern,
      subtitle: subtitle.trim() || undefined,
      bookLabel: bookLabel.trim(),
      description: description.trim() || undefined,
      learnerCoverImageUrl,
      printSettings: '{}',
      materialType: materialType || null,
      scopeLevel: scopeLevel || null,
      scopeContentId: hasScopedContent ? scopeContent?.id ?? null : null,
      sourceType,
      noticeMessage: noticeMessage.trim() || null,
      samplePdfUrl: samplePdfUrl.trim() || null,
      books: sortedRows.map((row) => ({
        orderIndex: row.orderIndex,
        title: row.rawTitle.trim() ? row.rawTitle : null,
        volumeLabel: row.volumeLabel,
        moduleIds: row.moduleIds,
      })),
    }
    createMutation.mutate(request)
  }

  const scopePickerLevel: HierarchyLevel | null = scopeLevel === 'CURRICULUM'
    ? 'curriculum'
    : scopeLevel === 'COURSE'
      ? 'course'
      : null

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/contents/books')}
            className="rounded-lg p-2 transition hover:bg-slate-100"
            aria-label="목록으로"
          >
            <ArrowLeft className="h-5 w-5 text-slate-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">교재 일괄 생성</h1>
            <p className="text-sm text-slate-500">권별 교재를 입력하고 다음 권 연결까지 한 번에 생성합니다.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit || createMutation.isPending}
          className="flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {sortedRows.length > 0 ? `${sortedRows.length}권 일괄 생성` : '일괄 생성'}
        </button>
      </div>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-slate-50/70 px-5 py-4">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">1</span>
            <div>
              <h2 className="font-semibold text-slate-900">공통 교재 정보</h2>
              <p className="text-xs text-slate-500">이번에 생성할 모든 권에 동일하게 적용됩니다.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 p-5 lg:grid-cols-3">
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-slate-700">교재 라벨 <span className="text-red-500">*</span></span>
            <input
              value={bookLabel}
              onChange={(event) => setBookLabel(event.target.value)}
              placeholder="예: Core A 체크업북"
              className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
            />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-slate-700">교재 유형</span>
            <select
              value={materialType}
              onChange={(event) => setMaterialType(event.target.value as PrintBookMaterialType | '')}
              className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
            >
              <option value="">미지정</option>
              <option value="CHECKUP_BOOK">체크업북</option>
              <option value="WORKBOOK">워크북</option>
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-slate-700">소스 유형</span>
            <select
              value={sourceType}
              onChange={(event) => setSourceType(event.target.value as PrintBookSourceType)}
              className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
            >
              <option value="GENERATED_PREVIEW">생성 프리뷰</option>
              <option value="EXTERNAL_DESIGN">외부 제작본</option>
            </select>
          </label>

          <label className="space-y-1.5">
            <span className="text-sm font-medium text-slate-700">노출 범위</span>
            <select
              value={scopeLevel}
              onChange={(event) => handleScopeLevelChange(event.target.value as PrintBookScopeLevel | '')}
              className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
            >
              <option value="">미지정</option>
              <option value="GLOBAL">전체</option>
              <option value="CURRICULUM">커리큘럼</option>
              <option value="COURSE">코스</option>
            </select>
          </label>

          {scopePickerLevel ? (
            <div className="space-y-1.5 lg:col-span-2">
              <span className="text-sm font-medium text-slate-700">
                범위 콘텐츠 <span className="text-red-500">*</span>
              </span>
              <button
                type="button"
                onClick={() => setIsScopePickerOpen(true)}
                className={`flex min-h-10 w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition ${
                  scopeContent
                    ? 'border-amber-300 bg-amber-50/60 ring-2 ring-amber-100'
                    : 'border-slate-300 bg-white hover:border-amber-400 hover:bg-amber-50/30'
                }`}
              >
                {scopeContent ? (
                  <>
                    <ContentLevelBadge level={scopeContent.hierarchyLevel} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-slate-800">{scopeContent.title}</span>
                      <span className="block text-xs text-slate-400">Content #{scopeContent.id}</span>
                    </span>
                    <span className="text-xs font-semibold text-amber-700">변경</span>
                  </>
                ) : (
                  <>
                    <span className="rounded-md bg-slate-100 p-1.5 text-slate-500"><Search className="h-4 w-4" /></span>
                    <span className="text-sm font-medium text-slate-500">
                      {scopeLevel === 'CURRICULUM' ? '커리큘럼을' : '코스를'} 검색해 선택하세요
                    </span>
                  </>
                )}
              </button>
            </div>
          ) : null}

          <label className="space-y-1.5">
            <span className="text-sm font-medium text-slate-700">제목 패턴</span>
            <input
              value={titlePattern}
              onChange={(event) => setTitlePattern(event.target.value)}
              className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
            />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-slate-700">부제</span>
            <input
              value={subtitle}
              onChange={(event) => setSubtitle(event.target.value)}
              placeholder="선택 입력"
              className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
            />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-slate-700">설명</span>
            <input
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="선택 입력"
              className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
            />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-slate-700">알림 문구</span>
            <input
              value={noticeMessage}
              onChange={(event) => setNoticeMessage(event.target.value)}
              placeholder="선택 입력"
              className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
            />
          </label>
          <label className="space-y-1.5 lg:col-span-2">
            <span className="text-sm font-medium text-slate-700">샘플 PDF URL</span>
            <input
              value={samplePdfUrl}
              onChange={(event) => setSamplePdfUrl(event.target.value)}
              placeholder="https://..."
              className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
            />
          </label>
          <div className="lg:col-span-3">
            <BookImageUploadField
              label="학습자 노출 이미지"
              value={learnerCoverImageUrl}
              onChange={setLearnerCoverImageUrl}
              previewAlt="학습자 노출 이미지 미리보기"
              helperText="이번에 생성하는 모든 권의 오늘 수업과 PauseQuiz에 같은 이미지가 적용됩니다."
            />
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 bg-slate-50/70 px-5 py-4">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">2</span>
            <div>
              <h2 className="font-semibold text-slate-900">권별 교재 정보</h2>
              <p className="text-xs text-slate-500">직접 추가하거나 CSV를 붙여넣을 수 있습니다.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPreviewRequested(true)}
            className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-700"
          >
            <TableProperties className="h-4 w-4" />
            미리보기 생성
          </button>
        </div>

        <div className="border-b border-slate-200 px-5 pt-4">
          <div className="flex gap-6" role="tablist" aria-label="권별 교재 입력 방식">
            <button
              type="button"
              role="tab"
              aria-selected={inputMode === 'manual'}
              onClick={() => {
                setInputMode('manual')
                setPreviewRequested(false)
              }}
              className={`flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-semibold transition ${
                inputMode === 'manual'
                  ? 'border-amber-500 text-slate-900'
                  : 'border-transparent text-slate-400 hover:text-slate-700'
              }`}
            >
              <ListPlus className="h-4 w-4" />
              한 줄씩 추가
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={inputMode === 'csv'}
              onClick={() => {
                setInputMode('csv')
                setPreviewRequested(false)
              }}
              className={`flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-semibold transition ${
                inputMode === 'csv'
                  ? 'border-amber-500 text-slate-900'
                  : 'border-transparent text-slate-400 hover:text-slate-700'
              }`}
            >
              <FileSpreadsheet className="h-4 w-4" />
              CSV 붙여넣기
            </button>
          </div>
        </div>

        {inputMode === 'manual' ? (
          <div className="space-y-3 p-5">
            {hasScopedContent && scopeContent ? (
              <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${
                isHierarchyError
                  ? 'bg-red-50 text-red-700'
                  : 'bg-amber-50 text-amber-800'
              }`}>
                {isHierarchyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers3 className="h-4 w-4" />}
                {isHierarchyLoading
                  ? '선택한 범위의 하위 모듈을 불러오는 중입니다.'
                  : isHierarchyError
                    ? '하위 모듈을 불러오지 못했습니다. 범위 콘텐츠를 다시 선택해 주세요.'
                    : `${scopeContent.title}의 하위 모듈 ${moduleOptions.length}개를 선택할 수 있습니다.`}
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                <Layers3 className="h-4 w-4" />
                하위 모듈을 선택하려면 노출 범위를 커리큘럼 또는 코스로 지정하고 범위 콘텐츠를 선택하세요.
              </div>
            )}

            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <div className="min-w-[860px]">
                <div className="grid grid-cols-[64px_160px_minmax(220px,1fr)_minmax(260px,1.2fr)_48px] gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2.5 text-xs font-semibold text-slate-500">
                  <span className="text-center">순서</span>
                  <span>권 표기 *</span>
                  <span>제목</span>
                  <span>하위 모듈</span>
                  <span className="sr-only">삭제</span>
                </div>

                {manualDrafts.map((draft, index) => {
                  const selectedModules = draft.moduleIds.flatMap((moduleId) => {
                    const module = modulesById.get(moduleId)
                    return module ? [module] : []
                  })
                  return (
                    <div
                      key={draft.id}
                      className="grid grid-cols-[64px_160px_minmax(220px,1fr)_minmax(260px,1.2fr)_48px] items-center gap-3 border-b border-slate-100 px-3 py-3 last:border-b-0"
                    >
                      <span className="mx-auto flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600">
                        {index + 1}
                      </span>
                      <input
                        value={draft.volumeLabel}
                        onChange={(event) => updateManualDraft(draft.id, { volumeLabel: event.target.value })}
                        placeholder={`${index + 1}권`}
                        aria-label={`${index + 1}번째 교재 권 표기`}
                        className="h-10 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
                      />
                      <input
                        value={draft.title}
                        onChange={(event) => updateManualDraft(draft.id, { title: event.target.value })}
                        placeholder="비워두면 제목 패턴 적용"
                        aria-label={`${index + 1}번째 교재 제목`}
                        className="h-10 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
                      />
                      <button
                        type="button"
                        onClick={() => setModulePickerDraftId(draft.id)}
                        disabled={!scopeContent || isHierarchyLoading || isHierarchyError || moduleOptions.length === 0}
                        className="flex min-h-10 min-w-0 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-left text-sm transition hover:border-amber-400 hover:bg-amber-50/40 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                      >
                        <Layers3 className="h-4 w-4 shrink-0 text-slate-400" />
                        {selectedModules.length > 0 ? (
                          <span className="min-w-0 flex-1 truncate font-medium text-slate-700">
                            {selectedModules.slice(0, 2).map((module) => module.title).join(', ')}
                            {selectedModules.length > 2 ? ` 외 ${selectedModules.length - 2}개` : ''}
                          </span>
                        ) : (
                          <span className="min-w-0 flex-1 truncate">모듈 선택</span>
                        )}
                        {selectedModules.length > 0 ? (
                          <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800">
                            {selectedModules.length}
                          </span>
                        ) : null}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeManualDraft(draft.id)}
                        aria-label={`${index + 1}번째 교재 삭제`}
                        className="rounded-lg p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )
                })}

                {manualDrafts.length === 0 ? (
                  <div className="px-5 py-10 text-center">
                    <ListPlus className="mx-auto h-7 w-7 text-slate-300" />
                    <p className="mt-2 text-sm text-slate-500">추가된 교재가 없습니다.</p>
                  </div>
                ) : null}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setManualDrafts((current) => [...current, createManualDraft()])}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 py-3 text-sm font-semibold text-slate-600 transition hover:border-amber-400 hover:bg-amber-50/40 hover:text-amber-800"
            >
              <Plus className="h-4 w-4" />
              교재 한 줄 추가
            </button>
          </div>
        ) : (
          <div className="space-y-3 p-5">
            <textarea
              value={csvText}
              onChange={(event) => setCsvText(event.target.value)}
              rows={8}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
            />
            <p className="text-xs text-slate-500">
              형식: orderIndex,volumeLabel,title,moduleIds / moduleIds는 | 로 구분하며, 비워두면 모듈 없이 교재 정보만 생성됩니다.
            </p>
          </div>
        )}
      </section>

      {previewRequested ? (
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <div>
              <h2 className="font-semibold text-slate-900">생성 미리보기</h2>
              <p className="mt-0.5 text-xs text-slate-500">표시된 순서대로 다음 권이 자동 연결됩니다.</p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-600">{sortedRows.length}권</span>
          </div>
          {parsed.errors.length > 0 ? (
            <div className="space-y-1 bg-red-50 px-5 py-3 text-sm text-red-700">
              {parsed.errors.map((error) => (
                <div key={`${error.rowNumber}-${error.code}`}>
                  {inputMode === 'manual' ? `${error.rowNumber}번째 교재` : `${error.rowNumber}행`}: {error.message}
                </div>
              ))}
            </div>
          ) : null}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">순서</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">제목</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">권</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">모듈</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">다음 권</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedRows.map((row, index) => {
                  const next = sortedRows[index + 1]
                  return (
                    <tr key={`${row.rowNumber}-${row.orderIndex}`} className="hover:bg-slate-50/60">
                      <td className="px-4 py-3 text-sm tabular-nums text-slate-600">{row.orderIndex}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{row.title}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{row.volumeLabel}</td>
                      <td className="px-4 py-3 text-center text-sm text-slate-600">{row.moduleIds.length}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {next ? `${next.title} (${next.volumeLabel})` : <span className="text-slate-400">마지막 권</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {isScopePickerOpen && scopePickerLevel ? (
        <ContentPickerModal
          allowedLevels={[scopePickerLevel]}
          title={`${scopeLevel === 'CURRICULUM' ? '커리큘럼' : '코스'} 범위 선택`}
          description="교재가 노출될 콘텐츠를 제목으로 검색해 선택하세요."
          onClose={() => setIsScopePickerOpen(false)}
          onSelect={handleScopeContentSelect}
        />
      ) : null}

      {activeModuleDraft ? (
        <BookBulkModulePicker
          options={moduleOptions}
          selectedIds={activeModuleDraft.moduleIds}
          onClose={() => setModulePickerDraftId(null)}
          onApply={(moduleIds) => {
            updateManualDraft(activeModuleDraft.id, { moduleIds })
            setModulePickerDraftId(null)
          }}
        />
      ) : null}
    </div>
  )
}
